import { FC, useRef, useState, useEffect, useContext } from 'react'
import { Camera, X, ArrowLeft } from 'lucide-react'
import PageContainer from 'shared/components/PageContainer'
import UserClickContext from 'shared/contexts/UserClickContext'
import { dbRegisterUser } from 'controllers/main'
import LocalizationContext from 'shared/contexts/LocalizationContext'

type Props = {
  sessionId: string
  locale: string
  onComplete: (name: string, mediaType: string, photo: Blob) => void
}

type Step = 'name' | 'photo'

const Registration: FC<Props> = ({ sessionId, locale, onComplete }) => {
  const [step, setStep] = useState<Step>('name')
  const [name, setName] = useState('')
  const [photo, setPhoto] = useState<Blob | null>(null)
  const [photoMediaType, setPhotoMediaType] = useState<string | null>(null)
  const [photoURL, setPhotoURL] = useState<string | null>(null)
  const [photoProcessing, setPhotoProcessing] = useState(false)
  const [cameraActive, setCameraActive] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { getString } = useContext(LocalizationContext)

  const { setClicked } = useContext(UserClickContext)

  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const nameInputRef = useRef<HTMLInputElement>(null)
  const streamRef = useRef<MediaStream | null>(null)

  // Helper to stop camera
  const stopCamera = () => {
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) {
        track.stop()
      }
      streamRef.current = null
      if (videoRef.current) {
        videoRef.current.srcObject = null
      }
    }
  }

  useEffect(() => {
    return () => {
      stopCamera()
    }
    // eslint-disable-next-line
  }, [])

  // Make sure video updates its srcObject after rendering
  useEffect(() => {
    if (cameraActive && streamRef.current && videoRef.current) {
      if (videoRef.current.srcObject !== streamRef.current) {
        videoRef.current.srcObject = streamRef.current
      }
    }
  }, [cameraActive])

  // Start camera and switch to selfie mode
  const handleStartCamera = async () => {
    setError(null)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' },
        audio: true
      })
      if (videoRef.current) {
        videoRef.current.srcObject = stream
      }
      streamRef.current = stream
      setCameraActive(true)
    } catch (err) {
      setError('Unable to access camera.')
    }
  }

  // Take a photo from webcam
  const handleTakePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return
    const video = videoRef.current
    const canvas = canvasRef.current
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext('2d')
    if (ctx) {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
      canvas.toBlob((blob) => {
        if (blob) {
          setPhoto(blob)
          setPhotoURL(URL.createObjectURL(blob))
          setPhotoMediaType(blob.type) // set the mediaType, e.g. "image/jpeg"
        }
      }, 'image/jpeg')
    }
    // stop webcam stream
    stopCamera()
    setCameraActive(false)
  }

  // Exit selfie mode and stop camera
  const handleExitCamera = () => {
    stopCamera()
    setCameraActive(false)
  }

  // Upload photo from device
  const handlePhotoInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setPhoto(file)
      setPhotoURL(URL.createObjectURL(file))
      setError(null)
      setPhotoMediaType(file.type || 'image/*')
    }
  }

  // Handle complete
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      if (name.trim() && photo && photoMediaType) {
        setPhotoProcessing(true)
        const userId = await dbRegisterUser(
          sessionId,
          name,
          photoMediaType,
          photo
        )
        if (userId) {
          setPhotoProcessing(false)
          onComplete(name.trim(), photoMediaType, photo)
        } else {
          setPhotoProcessing(false)
          setError('No faces could be detected in this photo.')
        }
      } else {
        setError('Please enter your name and provide a photo.')
      }
    } catch (err) {
      setError('An unexpected error occurred during registration.')
    }
  }

  // Remove current photo
  const handleRemovePhoto = () => {
    setPhoto(null)
    setPhotoURL(null)
    setPhotoMediaType(null)
    if (inputRef.current) inputRef.current.value = ''
  }

  // Handle name step continue
  const handleNameContinue = () => {
    if (name.trim()) {
      setClicked(true)
      setStep('photo')
      setError(null)
    }
  }

  // Handle Enter key on name input
  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && name.trim()) {
      e.preventDefault()
      handleNameContinue()
    }
  }

  // Handle back to name step
  const handleBackToName = () => {
    stopCamera()
    setCameraActive(false)
    setStep('name')
  }

  // Render the avatar section with camera selfie mode
  const renderAvatarSection = () => {
    // If in selfie (camera) mode, show the camera stream, Take and compact Exit
    if (cameraActive) {
      return (
        <div className='flex flex-col items-center'>
          <div className='relative w-40 h-40 mb-3'>
            <div className='w-40 h-40 rounded-full overflow-hidden bg-black border border-gray-200 flex items-center justify-center'>
              <video
                ref={videoRef}
                className='object-cover w-full h-full'
                width={256}
                height={256}
                autoPlay
                playsInline
                muted
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center',
                  display: 'block',
                  background: 'black',
                  borderRadius: '9999px'
                }}
              />
              {/* Compact X icon at the top-right corner */}
              <button
                type='button'
                aria-label='Close camera'
                onClick={handleExitCamera}
                className='absolute top-2 right-2 bg-white rounded-full p-1 shadow hover:bg-gray-100 focus:outline-none'
                tabIndex={0}
              >
                <X size={24} className='text-gray-500' />
              </button>
            </div>
          </div>
          <div className='flex flex-row gap-3'>
            <button
              type='button'
              className='py-1 px-4 rounded-full bg-blue-600 text-white text-base font-medium shadow'
              onClick={handleTakePhoto}
            >
              {getString('take_photo', locale)}
            </button>
          </div>
        </div>
      )
    }

    // If there's already an image selected/taken
    if (photoURL) {
      return (
        <div className='flex flex-col items-center'>
          <img
            src={photoURL}
            alt='Preview'
            className='rounded-full border border-gray-200 w-40 h-40 object-cover shadow mb-2'
          />
          <button
            type='button'
            className='text-sm text-blue-500 underline'
            onClick={handleRemovePhoto}
          >
            {getString('change', locale)}
          </button>
        </div>
      )
    }

    // DEFAULT: show icon and trigger Take Selfie or Upload
    return (
      <div className='flex flex-col items-center'>
        <div className='text-gray-500 text-base mt-2 mb-8'>
          {getString('take_or_upload_selfie', locale)}
        </div>
        <div className='relative w-40 h-40 flex items-center justify-center mb-4'>
          <div className='w-40 h-40 rounded-full border-2 border-gray-200 bg-white flex items-center justify-center shadow cursor-pointer'>
            <Camera
              className='text-gray-400'
              size={48}
              strokeWidth={1.8}
              onClick={handleStartCamera}
            />
          </div>
        </div>
        <div className='flex flex-col items-center'>
          <button
            type='button'
            className='text-base text-blue-600 hover:underline font-medium mb-1'
            onClick={handleStartCamera}
          >
            {getString('take_selfie', locale)}
          </button>
          <span className='text-gray-400 my-1'>{getString('or', locale)}</span>
          <label className='text-base text-blue-600 hover:underline font-medium mb-2 cursor-pointer'>
            <input
              type='file'
              accept='image/jpeg,image/png,image/heic'
              className='hidden'
              ref={inputRef}
              onChange={handlePhotoInput}
              tabIndex={-1}
              multiple={false}
            />
            {getString('upload_photo', locale)}
          </label>
        </div>
      </div>
    )
  }

  // Step 1: Name input
  if (step === 'name') {
    return (
      <PageContainer>
        <div className='flex flex-col items-center justify-start min-h-screen bg-gray-50 pt-16 sm:pt-24 px-4'>
          <div className='flex flex-col items-center bg-white rounded-xl shadow-sm p-6 sm:p-8 w-full max-w-sm gap-5'>
            <h2 className='text-xl font-semibold text-gray-800'>
              {getString('whats_your_name', locale)}
            </h2>

            <div className='w-full flex flex-col'>
              <input
                ref={nameInputRef}
                id='name-input'
                className='w-full px-4 py-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 placeholder-gray-400'
                type='text'
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={handleNameKeyDown}
                placeholder={getString('enter_your_name', locale)}
                autoFocus
              />
            </div>

            {error && (
              <div className='bg-red-100 text-red-700 rounded p-2 text-center text-sm w-full'>
                {error}
              </div>
            )}

            <button
              type='button'
              onClick={handleNameContinue}
              disabled={!name.trim()}
              className={`w-full py-3 rounded-full bg-blue-600 text-white font-semibold shadow transition text-base
                ${
                  !name.trim()
                    ? 'opacity-60 cursor-not-allowed'
                    : 'hover:bg-blue-700 active:bg-blue-800'
                }
              `}
            >
              {getString('enroll_user', locale)}
            </button>
          </div>
        </div>
      </PageContainer>
    )
  }

  // Step 2: Photo capture
  return (
    <PageContainer>
      <div className='flex flex-col items-center justify-start min-h-screen bg-gray-50 pt-16 sm:pt-24 px-4'>
        <form
          className='flex flex-col items-center bg-white rounded-xl shadow-sm p-6 sm:p-8 w-full max-w-sm gap-5'
          onSubmit={handleSubmit}
        >
          {/* Back button */}
          <button
            type='button'
            onClick={handleBackToName}
            className='self-start flex items-center gap-1 text-gray-500 hover:text-gray-700 text-sm -mt-1 -ml-1'
          >
            <ArrowLeft size={16} />
            <span>{getString('back', locale)}</span>
          </button>

          {/* Greeting with name */}
          <h2 className='text-lg font-medium text-gray-700 -mt-2'>
            {getString('hi', locale)} {name}
          </h2>

          {/* AVATAR AND PROMPT */}
          <div className='w-full flex flex-col items-center'>
            {renderAvatarSection()}
            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </div>

          {/* Loading indication and message during photo processing */}
          {photoProcessing && (
            <div className='flex items-center justify-center gap-2 text-blue-700 p-2 text-center text-sm w-full'>
              <svg
                className='animate-spin h-4 w-4 text-blue-700'
                fill='none'
                viewBox='0 0 24 24'
              >
                <circle
                  className='opacity-25'
                  cx='12'
                  cy='12'
                  r='10'
                  stroke='currentColor'
                  strokeWidth='4'
                ></circle>
                <path
                  className='opacity-75'
                  fill='currentColor'
                  d='M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z'
                ></path>
              </svg>
              <span>{getString('analysing_photo', locale)}</span>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className='bg-red-100 text-red-700 rounded p-2 text-center text-sm w-full'>
              {error}
            </div>
          )}

          {/* CONTINUE BUTTON only shown if a photo is selected/taken */}
          {photo && (
            <button
              type='submit'
              className={`w-full py-3 rounded-full bg-blue-600 text-white font-semibold shadow transition text-base ${
                photoProcessing
                  ? 'opacity-60 cursor-not-allowed'
                  : 'hover:bg-blue-700 active:bg-blue-800'
              }`}
              disabled={photoProcessing}
            >
              {getString('continue', locale)}
            </button>
          )}
        </form>
      </div>
    </PageContainer>
  )
}

export default Registration
